import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getUsers, destroyUser } from '../../../api/UsersAPI';
import { AuthContext } from '../../AuthContext';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import AppPaginator from '../../../components/AppPaginator';
import AppSearchBox from '../../../components/AppSearchBox';
import PerPageBlock from '../../../components/common/PerPageBlock';
import { getAvatar } from '../../../commonfunctions';
import AppToaster from '../../../components/AppToaster';

const UsersPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const successMessage = location.state?.message;

    const [usersList, setUsersList] = useState({ data: [], last_page: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [triggerFetch, setTriggerFetch] = useState(false);

    // Toaster state
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success!', message: successMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        setIsLoading(true);
        const filters = {
            paginate:true,
            page: currentPage,
            per_page: perPage,
            search_term: searchTermCompleted
        };
        getUsers(filters, (response) => {
            setIsLoading(false);
            if (response.users) {
                setUsersList(response.users);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, triggerFetch]);

    const handleAddClick = () => navigate('/club_portal/users/create');
    const handleEditClick = (id) => navigate(`/club_portal/users/edit/${id}`);

    const handleDeleteClick = (userToDelete) => {
        if (userToDelete.id === user.id) {
            Swal.fire('Action Forbidden', 'You cannot delete your own account.', 'error');
            return;
        }
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the user: <strong>${userToDelete.name}</strong></p><p>This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyUser(userToDelete, (response) => {
                    if (response.user) {
                        Swal.fire('Deleted!', 'The user has been deleted.', 'success');
                        setTriggerFetch(prev => !prev); // Re-fetch the list
                    } else {
                        Swal.fire('Error!', response?.response?.data?.message || 'Could not delete the user.', 'error');
                    }
                });
            }
        });
    };

    return (
        <main>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <div className="container-fluid">
                <AppBreadCrumb title="Users">
                    <li><a className="f-s-14 f-w-500" href="#">Access Control</a></li>
                    <li className="active"><a className="f-s-14 f-w-500" href="#">Users</a></li>
                </AppBreadCrumb>
                <div className="row">
                    <div className="col-12">
                        <div className="tab-wrapper mb-3">
                            <ul className="tabs">
                                <li className="tab-link active">All Users</li>
                                <li className="ms-md-auto">
                                    <div className="text-md-end d-flex flex-column flex-md-row align-items-md-center">
                                        <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                        <button className="text-nowrap btn btn-primary btn-sm mt-2 mt-md-0 ms-md-3" onClick={handleAddClick}>
                                            <i className="ti ti-plus f-s-18"></i> Add User
                                        </button>
                                    </div>
                                </li>
                            </ul>
                        </div>
                        <div className="content-wrapper">
                            <div className="d-flex m-md-3">
                                {usersList.data.length > 0 && (
                                    <>
                                        <AppPaginator totalPages={usersList.last_page} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                                        <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                    </>
                                )}
                                {isLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                            </div>
                            <div className="tabs-content active">
                                <div className="row">
                                    {!isLoading && (
                                        usersList.data.length > 0 ? usersList.data.map(eachUser => (
                                            <div className="col-md-6 col-xl-4 project-card" key={eachUser.id}>
                                                <div className="card hover-effect">
                                                    <div className="card-header">
                                                        <div className="d-flex align-items-center">
                                                            <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden">
                                                                <img alt={eachUser.name} className="img-fluid" src={getAvatar(eachUser.profile_pic_path)} />
                                                            </div>
                                                            <div className="flex-grow-1 ps-2">
                                                                <h6 className="m-0 text-dark f-w-600 txt-ellipsis-1">{eachUser.name}</h6>
                                                            </div>
                                                            <div className="dropdown">
                                                                <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown"><i className="ti ti-dots-vertical text-dark" /></button>
                                                                <ul className="dropdown-menu dropdown-menu-end">
                                                                    <li><a className="dropdown-item" onClick={() => handleEditClick(eachUser.id)}><i className="ti ti-edit text-success" /> Edit</a></li>
                                                                    {user.id !== eachUser.id &&
                                                                        <li><a className="dropdown-item" onClick={() => handleDeleteClick(eachUser)}><i className="ti ti-trash text-danger" /> Delete</a></li>
                                                                    }
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="card-body">
                                                        <div className="d-flex">
                                                            <div>
                                                                <h6 className="text-dark f-s-14">Phone: <span className="text-primary">{eachUser.phone_number}</span></h6>
                                                                {/* You can display roles here */}
                                                            </div>
                                                            <div className="flex-grow-1 text-end">
                                                                <p className="f-w-500 text-secondary">Id</p>
                                                                <h6 className="f-w-600">{eachUser.id}</h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )) : (
                                            <div className="card mb-3">
                                                <div className="row g-0">
                                                    <div className="col-md-8">
                                                        <div className="card-body">
                                                            <h5 className="card-title">No Users Found</h5>
                                                            <p className="card-text">Click "Add User" to add members to your club.</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <img alt="No data illustration" className="img-fluid" src="/assets/images/blog-app/09.jpg" />
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default UsersPage;

